import { useMutation, useQueryClient } from "@tanstack/react-query";
import { activateNoBody, activateProductWithSerials, deleteCustomerProduct, changeProductSerial } from "./endpoints";
import { patchDeviceSerialInCache } from "../cache-updaters/device-serials";
import { useCallback, useMemo, useState } from "react";
import { getErrorMessage } from "../helpers";
export const useProductActions = (customerId, productId, agreementDetailId) => {
    const queryClient = useQueryClient();
    const [activationFieldErrors, setActivationFieldErrors] = useState([]);
    const [activationMessage, setActivationMessage] = useState('');
    /* ----------------------- Helpers ----------------------- */
    const clearActivationFieldError = useCallback((dpadId) => {
        setActivationFieldErrors(prev => prev.filter(error => error.dpadId !== dpadId));
    }, []);
    const resetActivationErrors = useCallback(() => {
        setActivationFieldErrors([]);
        setActivationMessage('');
    }, []);
    const activationFieldMap = useMemo(() => {
        const map = new Map();
        activationFieldErrors.forEach(err => {
            map.set(err.dpadId, err.errorMessage);
        });
        return map;
    }, [activationFieldErrors]);
    const updateProductCache = useCallback((updated) => {
        queryClient.setQueryData(['customer-products', customerId], old => old
            ? old.map(product => product.productId === productId ? updated : product)
            : [updated]);
    }, [customerId, productId, queryClient]);
    /* ---------------- Activate with Serials ---------------- */
    const activateProductMutation = useMutation({
        mutationFn: ({ body }) => activateProductWithSerials({ productId, body }),
        onSuccess: (newProduct) => {
            updateProductCache(newProduct);
            setActivationFieldErrors([]);
            setActivationMessage('');
        },
        onError: (error) => {
            var _a;
            const data = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
            if (Array.isArray(data)) {
                setActivationFieldErrors(data);
                setActivationMessage('');
            }
            else {
                setActivationFieldErrors([]);
                setActivationMessage(getErrorMessage(error));
            }
        },
    });
    /* ---------------- Activate Agreement ---------------- */
    const activateAgreementMutation = useMutation({
        mutationFn: () => activateNoBody(productId),
        onSuccess: (updatedProduct) => {
            updateProductCache(updatedProduct);
        }
    });
    /* ---------------- Delete Product ---------------- */
    const deleteProductMutation = useMutation({
        mutationFn: () => deleteCustomerProduct(agreementDetailId),
        onSuccess: () => {
            queryClient.setQueryData(['customer-products', customerId], old => { var _a; return (_a = old === null || old === void 0 ? void 0 : old.filter(p => p.productId !== productId)) !== null && _a !== void 0 ? _a : []; });
            queryClient.invalidateQueries(['agreements', customerId]);
        },
    });
    /* ---------------- Change Device Serial (Swap) ---------------- */
    const changeSerialMutation = useMutation({
        mutationFn: async ({ productId, customerId, dpadId, serialNumber, reload, }) => {
            var _a, _b;
            const list = queryClient.getQueryData([
                'customer-products',
                customerId,
            ]);
            const product = list === null || list === void 0 ? void 0 : list.find(product => product.productId === productId);
            const oldSerial = (_b = (_a = product === null || product === void 0 ? void 0 : product.devices.find(device => device.dpadId === dpadId)) === null || _a === void 0 ? void 0 : _a.serialNumber) !== null && _b !== void 0 ? _b : null;
            const response = await changeProductSerial(dpadId, serialNumber);
            const productName = typeof response === 'object' && response && 'productName' in response
                ? response.productName
                : undefined;
            return {
                oldSerial,
                changedProduct: serialNumber,
                productName,
            };
        },
        onSuccess: (_res, vars) => {
            const { customerId, productId, dpadId, serialNumber, reload } = vars;
            if (reload) {
                queryClient.invalidateQueries(['customer-products', customerId]);
            }
            else {
                patchDeviceSerialInCache(queryClient, customerId, productId, dpadId, serialNumber);
            }
        },
    });
    return {
        activateWithSerials: (body) => activateProductMutation.mutate({ body }),
        activateAgreement: () => activateAgreementMutation.mutate(),
        deleteProduct: () => deleteProductMutation.mutate(),
        swapDeviceSerial: (dpadId, serialNumber, reload) => changeSerialMutation.mutate({
            productId,
            customerId,
            dpadId,
            serialNumber,
            reload,
        }),
        resetSwapDeviceSerial: changeSerialMutation.reset,
        resetActivationErrors,
        isProductFetching: activateProductMutation.isLoading ||
            activateAgreementMutation.isLoading ||
            deleteProductMutation.isLoading ||
            changeSerialMutation.isLoading,
        errors: {
            activateWithSerials: activationMessage,
            activationFieldMap,
            activateAgreement: getErrorMessage(activateAgreementMutation.error),
            delete: getErrorMessage(deleteProductMutation.error),
            swapDeviceSerial: getErrorMessage(changeSerialMutation.error),
        },
        clearActivationFieldError,
    };
};
